import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'motion/react';
import {
  Rocket,
  ShieldCheck,
  RotateCcw,
  Sparkles,
  Eye,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Send,
  RefreshCw,
  ExternalLink,
  Layers,
  ArrowRight,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

interface ReleaseItem {
  id: number;
  version: string;
  title: string;
  releaseNotes?: string;
  status: 'draft_beta' | 'active_production' | 'archived';
  isBetaOnly: boolean;
  buildHash?: string;
  releasedAt?: string;
  releasedBy?: string;
  createdAt: string;
}

interface TenantItem {
  id: string | number;
  name: string;
  apiKey: string;
  url?: string;
  status?: string;
}

interface ReleaseGovernanceHubProps {
  authHeader?: string | (() => Record<string, string>);
}

export const ReleaseGovernanceHub: React.FC<ReleaseGovernanceHubProps> = ({ authHeader }) => {
  const [releases, setReleases] = useState<ReleaseItem[]>([]);
  const [tenants, setTenants] = useState<TenantItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Canary Beta preview toggle
  const [isBetaPreview, setIsBetaPreview] = useState<boolean>(() => {
    return localStorage.getItem('kangavena_beta_preview') === '1';
  });

  // New Draft Release Form
  const [newVersion, setNewVersion] = useState<string>('');
  const [newTitle, setNewTitle] = useState<string>('');
  const [newReleaseNotes, setNewReleaseNotes] = useState<string>('');
  const [isDraftSubmitting, setIsDraftSubmitting] = useState<boolean>(false);

  // Impersonation
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [isImpersonating, setIsImpersonating] = useState<boolean>(false);
  const [expandedRelId, setExpandedRelId] = useState<number | null>(null);

  const getHeaders = useCallback(() => {
    let h: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (typeof authHeader === 'function') {
      try {
        const gen = authHeader();
        if (gen && typeof gen === 'object') {
          h = { ...h, ...gen };
        }
      } catch (err) {
        console.error('[ReleaseHub] Error executing authHeader():', err);
      }
    } else if (typeof authHeader === 'string' && authHeader.trim().length > 0) {
      h['Authorization'] = authHeader.startsWith('Bearer ') ? authHeader : `Bearer ${authHeader}`;
    }

    if (!h['Authorization']) {
      const adminKey = sessionStorage.getItem('adminToken') || localStorage.getItem('kangavena_admin_token') || sessionStorage.getItem('admin_token');
      if (adminKey) {
        h['Authorization'] = adminKey.startsWith('Bearer ') ? adminKey : `Bearer ${adminKey}`;
      }
    }
    const godToken = sessionStorage.getItem('godModeToken');
    if (godToken && !h['X-God-Mode-Token']) {
      h['X-God-Mode-Token'] = godToken;
    }
    return h;
  }, [authHeader]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getHeaders();
      const [resReleases, resTenants] = await Promise.all([
        fetch('/api/system/releases', { headers }).then(r => r.json()).catch(() => ({ releases: [] })),
        fetch('/api/system/impersonate/tenants', { headers }).then(r => r.json()).catch(() => ({ tenants: [] })),
      ]);

      if (resReleases.releases) {
        setReleases(resReleases.releases);
      }
      if (resTenants.tenants) {
        setTenants(resTenants.tenants);
        if (resTenants.tenants.length > 0 && !selectedTenantId) {
          setSelectedTenantId(String(resTenants.tenants[0].id));
        }
      }
    } catch (err: any) {
      console.error('[ReleaseHub] Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [getHeaders, selectedTenantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggleBetaPreview = () => {
    const nextVal = !isBetaPreview;
    setIsBetaPreview(nextVal);
    if (nextVal) {
      localStorage.setItem('kangavena_beta_preview', '1');
      setStatusMessage({ text: '🧪 Canary Beta Preview Mode ACTIVATED. You are now testing draft beta releases in production.', type: 'success' });
    } else {
      localStorage.removeItem('kangavena_beta_preview');
      setStatusMessage({ text: 'Standard production preview restored.', type: 'success' });
    }
    setTimeout(() => setStatusMessage(null), 5000);
  };

  const handleCreateDraft = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newVersion || !newTitle) return;

    setIsDraftSubmitting(true);
    setStatusMessage(null);
    try {
      const res = await fetch('/api/system/releases/create', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          version: newVersion.trim(),
          title: newTitle.trim(),
          releaseNotes: newReleaseNotes.trim(),
          isBetaOnly: true,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to create draft release');
      }

      setStatusMessage({ text: `✓ Draft release ${newVersion} created! You can now test it in Canary Beta mode.`, type: 'success' });
      setNewVersion('');
      setNewTitle('');
      setNewReleaseNotes('');
      fetchData();
    } catch (err: any) {
      setStatusMessage({ text: `❌ ${err.message || 'Error creating draft release'}`, type: 'error' });
    } finally {
      setIsDraftSubmitting(false);
      setTimeout(() => setStatusMessage(null), 6000);
    }
  };

  const handlePublishRelease = async (version: string) => {
    if (!confirm(`Are you sure you want to promote ${version} to ACTIVE PRODUCTION?\nAll tenants will immediately receive the 1-click update prompt.`)) {
      return;
    }

    setActionLoading(`publish-${version}`);
    setStatusMessage(null);
    try {
      const res = await fetch('/api/system/releases/publish', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ version }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to publish release');
      }

      setStatusMessage({ text: `🚀 Release ${version} is now LIVE in ACTIVE PRODUCTION! Broadcast signal dispatched.`, type: 'success' });
      fetchData();
    } catch (err: any) {
      setStatusMessage({ text: `❌ ${err.message || 'Publish failed'}`, type: 'error' });
    } finally {
      setActionLoading(null);
      setTimeout(() => setStatusMessage(null), 6000);
    }
  };

  const handleRollback = async (version: string) => {
    if (!confirm(`⚠️ CRITICAL: Instant Rollback to ${version}?\nThis will revert active production to ${version} in <10ms.`)) {
      return;
    }

    setActionLoading(`rollback-${version}`);
    setStatusMessage(null);
    try {
      const res = await fetch('/api/system/releases/rollback', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ targetVersion: version }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Rollback failed');
      }

      setStatusMessage({ text: `⏪ Instant Rollback to ${version} complete! Active production reverted in <10ms.`, type: 'success' });
      fetchData();
    } catch (err: any) {
      setStatusMessage({ text: `❌ ${err.message || 'Rollback failed'}`, type: 'error' });
    } finally {
      setActionLoading(null);
      setTimeout(() => setStatusMessage(null), 6000);
    }
  };

  const handleImpersonateTenant = async () => {
    if (!selectedTenantId) return;

    setIsImpersonating(true);
    setStatusMessage(null);
    try {
      const res = await fetch('/api/system/impersonate/token', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ tenantId: selectedTenantId }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to generate impersonation token');
      }

      // Save impersonation session token into localStorage & sessionStorage
      sessionStorage.setItem('tenet_token', data.token);
      sessionStorage.setItem('tenet_auth_token', data.token);
      sessionStorage.setItem('tenet_client_token', data.token);
      localStorage.setItem('tenet_client_token', data.token);
      localStorage.setItem('tenet_token', data.token);
      sessionStorage.setItem('tenet_client_name', data.tenant.name);
      sessionStorage.setItem('tenet_api_key', data.tenant.apiKey);
      sessionStorage.setItem('tenet_is_impersonated', '1');
      localStorage.setItem('tenet_is_impersonated', '1');

      // Open tenet workspace in new tab with token and impersonated flag in URL
      window.open(`/tenet?token=${encodeURIComponent(data.token)}&impersonated=1`, '_blank');
      setStatusMessage({ text: `👁️ Impersonation active for ${data.tenant.name}. Opened in new window.`, type: 'success' });
    } catch (err: any) {
      setStatusMessage({ text: `❌ ${err.message || 'Impersonation failed'}`, type: 'error' });
    } finally {
      setIsImpersonating(false);
      setTimeout(() => setStatusMessage(null), 6000);
    }
  };

  const activeRelease = releases.find(r => r.status === 'active_production');
  const draftReleases = releases.filter(r => r.status === 'draft_beta');
  const archivedReleases = releases.filter(r => r.status === 'archived');

  return (
    <div className="space-y-6">
      {/* Top Banner / HUD */}
      <div className="relative overflow-hidden rounded-2xl border border-cyan-500/30 bg-gradient-to-r from-slate-950 via-slate-900 to-cyan-950/40 p-6 shadow-2xl backdrop-blur-xl">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center shrink-0 shadow-[0_0_30px_rgba(6,182,212,0.3)]">
              <Rocket className="text-cyan-400 animate-pulse" size={24} />
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-black text-white font-mono tracking-tight">
                  ZERO-DOWNTIME RELEASE GOVERNANCE
                </h1>
                <span className="px-2.5 py-0.5 rounded-full bg-cyan-500/20 border border-cyan-400/40 text-[10px] font-mono font-bold text-cyan-300">
                  RULE 23 COMPLIANT
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-1 font-mono">
                Atomic Staging Builds • Canary Beta Testing • God-Mode Impersonation • Instant &lt;10ms Rollbacks
              </p>
            </div>
          </div>

          {/* Canary Beta Toggle */}
          <div className="flex items-center gap-4 bg-slate-950/80 border border-cyan-500/30 p-3 rounded-xl">
            <div>
              <div className="text-[11px] font-mono font-bold text-cyan-300 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
                CANARY BETA PREVIEW
              </div>
              <div className="text-[10px] text-slate-400">
                {isBetaPreview ? 'Active: You see draft beta builds' : 'Disabled: Viewing production'}
              </div>
            </div>
            <button
              onClick={handleToggleBetaPreview}
              className={`w-12 h-6 rounded-full transition-all duration-300 flex items-center px-1 cursor-pointer ${
                isBetaPreview ? 'bg-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.6)]' : 'bg-slate-800 border border-slate-700'
              }`}
            >
              <div className={`w-4 h-4 bg-white rounded-full transition-transform duration-300 ${isBetaPreview ? 'translate-x-6' : 'translate-x-0'}`} />
            </button>
          </div>
        </div>

        {/* Status Toast */}
        {statusMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mt-4 p-3 rounded-xl border text-xs font-mono font-bold flex items-center gap-2 ${
              statusMessage.type === 'success'
                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                : 'bg-rose-500/10 border-rose-500/30 text-rose-300'
            }`}
          >
            {statusMessage.type === 'success' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
            <span>{statusMessage.text}</span>
          </motion.div>
        )}
      </div>

      {/* Grid: 2 Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left 2 Cols: Active & Historical Releases */}
        <div className="lg:col-span-2 space-y-6">
          {/* Active Production Release Card */}
          <div className="rounded-2xl border border-emerald-500/40 bg-slate-950/80 p-6 shadow-[0_0_30px_rgba(16,185,129,0.15)] relative overflow-hidden">
            <div className="flex items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-2">
                <span className="flex h-3 w-3 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
                </span>
                <span className="text-xs font-mono font-black text-emerald-400 uppercase tracking-wider">
                  Active Production Release (All Tenants)
                </span>
              </div>
              <button
                onClick={fetchData}
                className="p-1.5 rounded-lg border border-white/10 hover:bg-white/5 text-slate-400 hover:text-white transition-colors"
                title="Refresh releases"
              >
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              </button>
            </div>

            {activeRelease ? (
              <div className="space-y-3">
                <div className="flex items-baseline gap-3 flex-wrap">
                  <span className="text-2xl font-mono font-black text-emerald-300">
                    {activeRelease.version}
                  </span>
                  <span className="text-base font-semibold text-white">
                    {activeRelease.title}
                  </span>
                </div>
                {activeRelease.releaseNotes && (
                  <div className="p-3 rounded-xl bg-slate-900/90 border border-white/5 text-xs font-mono text-slate-300 whitespace-pre-wrap leading-relaxed">
                    {activeRelease.releaseNotes}
                  </div>
                )}
                <div className="flex items-center justify-between text-[11px] font-mono text-slate-400 pt-2 border-t border-white/5">
                  <span>Released: {new Date(activeRelease.releasedAt || activeRelease.createdAt).toLocaleString()}</span>
                  <span>Published by: {activeRelease.releasedBy || 'master_admin'}</span>
                </div>
              </div>
            ) : (
              <div className="text-sm font-mono text-slate-400 py-4">
                No release marked active_production. Seeded baseline v6.5.0 in effect.
              </div>
            )}
          </div>

          {/* Draft Beta Releases (Canary Stage) */}
          {draftReleases.length > 0 && (
            <div className="rounded-2xl border border-cyan-500/30 bg-slate-950/80 p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-cyan-400" />
                <h3 className="text-sm font-mono font-bold text-cyan-300 uppercase tracking-wider">
                  Draft Beta Releases (Canary Sandbox)
                </h3>
              </div>

              <div className="space-y-3">
                {draftReleases.map(draft => {
                  const isExpanded = expandedRelId === draft.id;
                  return (
                    <div
                      key={draft.id}
                      className="p-4 rounded-xl border border-cyan-500/20 bg-slate-900/60 transition-all"
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-mono font-bold text-cyan-400">{draft.version}</span>
                            <span className="text-xs font-semibold text-white">{draft.title}</span>
                          </div>
                          {draft.releaseNotes && (
                            <button
                              type="button"
                              onClick={() => setExpandedRelId(isExpanded ? null : draft.id)}
                              className="mt-1.5 flex items-center gap-1 text-[11px] text-cyan-400/80 hover:text-cyan-300 font-mono transition cursor-pointer"
                            >
                              <span>{isExpanded ? 'Hide Release Features' : 'View Release Features'}</span>
                              {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                            </button>
                          )}
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={() => handlePublishRelease(draft.version)}
                            disabled={actionLoading === `publish-${draft.version}`}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-mono font-bold text-xs shadow-[0_0_15px_rgba(16,185,129,0.4)] transition-all cursor-pointer disabled:opacity-50"
                          >
                            <Send size={12} />
                            <span>{actionLoading === `publish-${draft.version}` ? 'Broadcasting...' : 'Publish & Broadcast'}</span>
                          </button>
                        </div>
                      </div>

                      {isExpanded && draft.releaseNotes && (
                        <div className="mt-3 p-3 rounded-lg bg-slate-950/80 border border-slate-800 text-[11px] font-mono text-slate-300 whitespace-pre-wrap leading-relaxed shadow-inner max-h-48 overflow-y-auto">
                          <div className="text-[10px] font-bold text-cyan-400 mb-1 flex items-center gap-1">
                            <Sparkles size={11} /> Draft Release Features & Notes:
                          </div>
                          {draft.releaseNotes}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Rollback Timeline */}
          <div className="rounded-2xl border border-slate-800 bg-slate-950/80 p-6 space-y-4">
            <div className="flex items-center gap-2">
              <RotateCcw className="w-4 h-4 text-purple-400" />
              <h3 className="text-sm font-mono font-bold text-purple-300 uppercase tracking-wider">
                Instant Rollback Timeline (&lt;10ms)
              </h3>
            </div>

            {archivedReleases.length === 0 ? (
              <p className="text-xs font-mono text-slate-500 py-2">
                No archived releases yet. When new versions are published, previous stable builds appear here for instant 1-click rollback.
              </p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                {archivedReleases.map(rel => {
                  const isExpanded = expandedRelId === rel.id;
                  return (
                    <div
                      key={rel.id}
                      className="p-3 rounded-xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 font-mono text-xs flex-wrap">
                            <span className="font-bold text-slate-300">{rel.version}</span>
                            <span className="text-slate-400 truncate max-w-xs">{rel.title}</span>
                          </div>
                          <div className="text-[10px] font-mono text-slate-500 mt-0.5 flex items-center gap-2">
                            <span>Archived • Created {new Date(rel.createdAt).toLocaleDateString()}</span>
                            {rel.releaseNotes && (
                              <button
                                type="button"
                                onClick={() => setExpandedRelId(isExpanded ? null : rel.id)}
                                className="text-purple-400/80 hover:text-purple-300 flex items-center gap-0.5 cursor-pointer ml-1 font-mono text-[10px]"
                              >
                                <span>{isExpanded ? 'Hide' : 'Features'}</span>
                                {isExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                              </button>
                            )}
                          </div>
                        </div>

                        <button
                          onClick={() => handleRollback(rel.version)}
                          disabled={actionLoading === `rollback-${rel.version}`}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-purple-500/40 hover:bg-purple-500/20 text-purple-300 font-mono text-[11px] font-bold transition-all cursor-pointer disabled:opacity-50 shrink-0"
                        >
                          <RotateCcw size={11} />
                          <span>{actionLoading === `rollback-${rel.version}` ? 'Rolling back...' : 'Rollback'}</span>
                        </button>
                      </div>

                      {isExpanded && rel.releaseNotes && (
                        <div className="mt-2.5 p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 text-[11px] font-mono text-slate-300 whitespace-pre-wrap leading-relaxed shadow-inner max-h-40 overflow-y-auto">
                          {rel.releaseNotes}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right 1 Col: God-Mode Impersonation & Draft Release Form */}
        <div className="space-y-6">
          {/* God-Mode Tenant Impersonation */}
          <div className="rounded-2xl border border-indigo-500/30 bg-slate-950/80 p-6 space-y-4">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-indigo-400" />
              <h3 className="text-sm font-mono font-bold text-indigo-300 uppercase tracking-wider">
                God-Mode Impersonation
              </h3>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed font-mono">
              View the Tenet Dashboard through the eyes of any registered tenant without needing their credentials.
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest mb-1.5">
                  Select Tenant:
                </label>
                <select
                  value={selectedTenantId}
                  onChange={e => setSelectedTenantId(e.target.value)}
                  disabled={tenants.length === 0}
                  className="w-full bg-black/60 border border-indigo-500/30 rounded-xl px-3 py-2.5 text-xs text-white font-mono outline-none focus:ring-2 focus:ring-indigo-500 transition-all disabled:opacity-60 cursor-pointer"
                >
                  {tenants.length === 0 ? (
                    <option value="" disabled className="bg-slate-900 text-slate-500">
                      {loading ? 'Loading registered tenants...' : 'No registered tenants found'}
                    </option>
                  ) : (
                    tenants.map(t => (
                      <option key={t.id} value={String(t.id)} className="bg-slate-900 text-white">
                        {t.name} (ID: {t.id})
                      </option>
                    ))
                  )}
                </select>
              </div>

              <button
                onClick={handleImpersonateTenant}
                disabled={isImpersonating || !selectedTenantId}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-mono font-bold text-xs shadow-[0_0_20px_rgba(99,102,241,0.3)] transition-all cursor-pointer disabled:opacity-50"
              >
                <ExternalLink size={13} />
                <span>{isImpersonating ? 'Launching Session...' : 'Impersonate Tenant Dashboard'}</span>
              </button>
            </div>
          </div>

          {/* Draft New Beta Release Form */}
          <div className="rounded-2xl border border-cyan-500/30 bg-slate-950/80 p-6 space-y-4">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-cyan-400" />
              <h3 className="text-sm font-mono font-bold text-cyan-300 uppercase tracking-wider">
                Create Draft Beta Release
              </h3>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed font-mono">
              Register a draft version for Canary Beta testing prior to active production release.
            </p>

            <form onSubmit={handleCreateDraft} className="space-y-3">
              <div>
                <label className="block text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest mb-1">
                  Version Tag (e.g. v6.6.0)
                </label>
                <input
                  type="text"
                  value={newVersion}
                  onChange={e => setNewVersion(e.target.value)}
                  placeholder="v6.6.0"
                  className="w-full bg-black/60 border border-white/10 rounded-xl px-3 py-2 text-xs text-white font-mono outline-none focus:border-cyan-500"
                  required
                />
              </div>

              <div>
                <label className="block text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest mb-1">
                  Release Title
                </label>
                <input
                  type="text"
                  value={newTitle}
                  onChange={e => setNewTitle(e.target.value)}
                  placeholder="e.g. Real-Time Telemetry & Catalog Sync"
                  className="w-full bg-black/60 border border-white/10 rounded-xl px-3 py-2 text-xs text-white font-mono outline-none focus:border-cyan-500"
                  required
                />
              </div>

              <div>
                <label className="block text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest mb-1">
                  Release Notes
                </label>
                <textarea
                  rows={3}
                  value={newReleaseNotes}
                  onChange={e => setNewReleaseNotes(e.target.value)}
                  placeholder="• Feature 1&#10;• Bugfix 2&#10;• Performance optimization"
                  className="w-full bg-black/60 border border-white/10 rounded-xl px-3 py-2 text-xs text-white font-mono outline-none focus:border-cyan-500 resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={isDraftSubmitting}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-mono font-bold text-xs shadow-[0_0_20px_rgba(6,182,212,0.3)] transition-all cursor-pointer disabled:opacity-50"
              >
                <ArrowRight size={13} />
                <span>{isDraftSubmitting ? 'Creating Draft...' : 'Create Draft Beta'}</span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReleaseGovernanceHub;
